export interface PdfSearchBounds {
  height: number
  left: number
  top: number
  width: number
}

export interface PdfSearchMatch {
  bounds: PdfSearchBounds[]
  id: string
  matchEnd: number
  matchStart: number
  pageNumber: number
}

interface PdfSearchTextComponent {
  end: number
  geometry: PdfSearchTextGeometry
  start: number
}

interface PdfSearchTextGeometry extends PdfSearchBounds {
  alongX: number
  alongY: number
  ascent: number
  baselineX: number
  baselineY: number
  descent: number
  itemWidth: number
  upX: number
  upY: number
}

interface PdfSearchPage {
  components: PdfSearchTextComponent[]
  pageNumber: number
  text: string
}

interface PdfTextItem {
  fontName: string
  hasEOL: boolean
  height: number
  str: string
  transform: number[]
  width: number
}

interface PdfTextStyle {
  ascent?: number
  descent?: number
  vertical?: boolean
}

interface PdfSearchDocumentCache {
  pagePromises: Map<number, Promise<PdfSearchPage>>
  pagesPromise?: Promise<PdfSearchPage[]>
}

interface PdfSearchPageProxy {
  getTextContent: () => Promise<{
    items: unknown[]
    styles: Record<string, PdfTextStyle>
  }>
  getViewport: (options: { scale: number }) => {
    transform: number[]
  }
}

export interface PdfSearchDocumentProxy {
  getPage: (pageNumber: number) => Promise<PdfSearchPageProxy>
  numPages: number
}

const searchDocumentCaches = new WeakMap<
  PdfSearchDocumentProxy,
  PdfSearchDocumentCache
>()
const SEARCH_EXTRACTION_CONCURRENCY = 4
// One frame at 120hz is ~8ms: slices under that let keystrokes and pointer
// events interleave with a scan instead of queueing behind it.
const SEARCH_SLICE_BUDGET_MS = 8

// scheduler.yield resumes ahead of other queued work; setTimeout is the
// fallback for browsers without it.
const yieldToMain = (): Promise<void> => {
  const { scheduler } = globalThis as {
    scheduler?: { yield?: () => Promise<void> }
  }
  if (typeof scheduler?.yield === 'function') {
    return scheduler.yield()
  }
  return new Promise((resolve) => {
    setTimeout(resolve, 0)
  })
}

const EMPTY_SEARCH_PAGE = (pageNumber: number): PdfSearchPage => ({
  components: [],
  pageNumber,
  text: '',
})

const isFiniteNumberArray = (
  value: unknown,
  minimumLength: number,
): value is number[] =>
  Array.isArray(value) &&
  value.length >= minimumLength &&
  value.every((entry) => typeof entry === 'number' && Number.isFinite(entry))

const isPdfTextItem = (item: unknown): item is PdfTextItem => {
  if (typeof item !== 'object' || item === null) {
    return false
  }

  const candidate = item as Partial<PdfTextItem>
  return (
    typeof candidate.str === 'string' &&
    typeof candidate.fontName === 'string' &&
    typeof candidate.hasEOL === 'boolean' &&
    typeof candidate.width === 'number' &&
    Number.isFinite(candidate.width) &&
    typeof candidate.height === 'number' &&
    Number.isFinite(candidate.height) &&
    isFiniteNumberArray(candidate.transform, 6)
  )
}

const multiplyTransforms = (
  first: number[],
  second: number[],
): [number, number, number, number, number, number] => [
  first[0]! * second[0]! + first[2]! * second[1]!,
  first[1]! * second[0]! + first[3]! * second[1]!,
  first[0]! * second[2]! + first[2]! * second[3]!,
  first[1]! * second[2]! + first[3]! * second[3]!,
  first[0]! * second[4]! + first[2]! * second[5]! + first[4]!,
  first[1]! * second[4]! + first[3]! * second[5]! + first[5]!,
]

const getTextRangeBounds = (
  geometry: Omit<PdfSearchTextGeometry, keyof PdfSearchBounds>,
  startRatio = 0,
  endRatio = 1,
): PdfSearchBounds | null => {
  const {
    alongX,
    alongY,
    ascent,
    baselineX,
    baselineY,
    descent,
    itemWidth,
    upX,
    upY,
  } = geometry
  const startOffset = itemWidth * Math.max(0, Math.min(1, startRatio))
  const endOffset = itemWidth * Math.max(0, Math.min(1, endRatio))
  const topStart = [
    baselineX + upX * ascent + alongX * startOffset,
    baselineY + upY * ascent + alongY * startOffset,
  ] as const
  const topEnd = [
    baselineX + upX * ascent + alongX * endOffset,
    baselineY + upY * ascent + alongY * endOffset,
  ] as const
  const bottomStart = [
    baselineX - upX * descent + alongX * startOffset,
    baselineY - upY * descent + alongY * startOffset,
  ] as const
  const bottomEnd = [
    baselineX - upX * descent + alongX * endOffset,
    baselineY - upY * descent + alongY * endOffset,
  ] as const
  const xCoordinates = [topStart[0], topEnd[0], bottomStart[0], bottomEnd[0]]
  const yCoordinates = [topStart[1], topEnd[1], bottomStart[1], bottomEnd[1]]
  const left = Math.min(...xCoordinates)
  const right = Math.max(...xCoordinates)
  const top = Math.min(...yCoordinates)
  const bottom = Math.max(...yCoordinates)

  if (![left, right, top, bottom].every(Number.isFinite)) {
    return null
  }

  return {
    height: Math.max(1, bottom - top),
    left,
    top,
    width: Math.max(1, right - left),
  }
}

const getTextItemGeometry = (
  item: PdfTextItem,
  viewportTransform: number[],
  styles: Record<string, PdfTextStyle>,
): PdfSearchTextGeometry | null => {
  if (!isFiniteNumberArray(viewportTransform, 6)) {
    return null
  }

  const transformed = multiplyTransforms(viewportTransform, item.transform)
  const style = styles[item.fontName]
  const fontHeight = Math.hypot(transformed[2], transformed[3])
  const itemWidth = Math.abs(style?.vertical ? item.height : item.width)
  if (
    !Number.isFinite(fontHeight) ||
    fontHeight <= 0 ||
    !Number.isFinite(itemWidth) ||
    itemWidth <= 0
  ) {
    return null
  }

  let angle = Math.atan2(transformed[1], transformed[0])
  if (style?.vertical) {
    angle += Math.PI / 2
  }

  const ascentRatio =
    style?.ascent ?? (style?.descent !== undefined ? 1 + style.descent : 1)
  const safeAscentRatio = Number.isFinite(ascentRatio)
    ? Math.max(0, Math.min(1, ascentRatio))
    : 1
  const ascent = fontHeight * safeAscentRatio
  const descent = fontHeight - ascent
  const baselineX = transformed[4]
  const baselineY = transformed[5]
  const alongX = Math.cos(angle)
  const alongY = Math.sin(angle)
  const upX = Math.sin(angle)
  const upY = -Math.cos(angle)

  const geometry = {
    alongX,
    alongY,
    ascent,
    baselineX,
    baselineY,
    descent,
    itemWidth,
    upX,
    upY,
  }
  const bounds = getTextRangeBounds(geometry)
  if (!bounds) {
    return null
  }

  return {
    ...bounds,
    ...geometry,
  }
}

const getTextSeparator = (previousItem: PdfTextItem | null) => {
  return previousItem?.hasEOL ? '\n' : ''
}

const extractSearchPage = async (
  document: PdfSearchDocumentProxy,
  pageNumber: number,
): Promise<PdfSearchPage> => {
  try {
    const page = await document.getPage(pageNumber)
    const textContent = await page.getTextContent()
    const viewportTransform = page.getViewport({ scale: 1 }).transform
    const styles = textContent.styles
    const components: PdfSearchTextComponent[] = []
    let text = ''
    let previousItem: PdfTextItem | null = null

    for (const item of textContent.items) {
      if (!isPdfTextItem(item)) {
        continue
      }

      text += getTextSeparator(previousItem)
      const start = text.length
      text += item.str
      const end = text.length
      const geometry = getTextItemGeometry(item, viewportTransform, styles)
      if (geometry && end > start) {
        components.push({ end, geometry, start })
      }
      previousItem = item
    }

    return { components, pageNumber, text }
  } catch {
    // A damaged page should not make find unusable for the rest of the PDF.
    return EMPTY_SEARCH_PAGE(pageNumber)
  }
}

const getSearchPages = (document: PdfSearchDocumentProxy) => {
  const cached = searchDocumentCaches.get(document)
  const cache: PdfSearchDocumentCache = cached ?? {
    pagePromises: new Map<number, Promise<PdfSearchPage>>(),
  }
  if (!cached) {
    searchDocumentCaches.set(document, cache)
  }
  if (cache.pagesPromise) {
    return cache.pagesPromise
  }

  const pages = Array<PdfSearchPage>(document.numPages)
  let nextPageNumber = 1
  const extractNextPage = async (): Promise<void> => {
    while (nextPageNumber <= document.numPages) {
      const pageNumber = nextPageNumber
      nextPageNumber += 1
      let pagePromise = cache.pagePromises.get(pageNumber)
      if (!pagePromise) {
        pagePromise = extractSearchPage(document, pageNumber)
        cache.pagePromises.set(pageNumber, pagePromise)
      }
      pages[pageNumber - 1] = await pagePromise
    }
  }
  const workerCount = Math.min(SEARCH_EXTRACTION_CONCURRENCY, document.numPages)
  cache.pagesPromise = Promise.all(
    Array.from({ length: workerCount }, () => extractNextPage()),
  ).then(() => pages)
  return cache.pagesPromise
}

const findPageMatches = (
  page: PdfSearchPage,
  query: string,
): PdfSearchMatch[] => {
  const normalizedQuery = query.toLowerCase()
  if (!normalizedQuery) {
    return []
  }

  const normalizedText = page.text.toLowerCase()
  const { components } = page
  const matches: PdfSearchMatch[] = []
  let searchFrom = 0
  // Components sit in text order and never overlap (extractSearchPage appends
  // them as the page text grows), and matches only move forward — so one
  // cursor over the components replaces a scan of all of them per match.
  let componentCursor = 0

  while (searchFrom <= normalizedText.length - normalizedQuery.length) {
    const matchStart = normalizedText.indexOf(normalizedQuery, searchFrom)
    if (matchStart === -1) {
      break
    }
    const matchEnd = matchStart + normalizedQuery.length

    while (
      componentCursor < components.length &&
      components[componentCursor]!.end <= matchStart
    ) {
      componentCursor += 1
    }

    const bounds: PdfSearchBounds[] = []
    for (
      let componentIndex = componentCursor;
      componentIndex < components.length;
      componentIndex += 1
    ) {
      const component = components[componentIndex]!
      if (component.start >= matchEnd) {
        break
      }
      const overlapStart = Math.max(component.start, matchStart)
      const overlapEnd = Math.min(component.end, matchEnd)
      const componentLength = component.end - component.start
      const rangeBounds = getTextRangeBounds(
        component.geometry,
        (overlapStart - component.start) / componentLength,
        (overlapEnd - component.start) / componentLength,
      )
      if (rangeBounds) {
        bounds.push(rangeBounds)
      }
    }

    if (bounds.length > 0) {
      matches.push({
        bounds,
        id: `${page.pageNumber}:${matchStart}:${matchEnd}`,
        matchEnd,
        matchStart,
        pageNumber: page.pageNumber,
      })
    }
    searchFrom = matchEnd
  }

  return matches
}

export const searchPdfDocument = async (
  document: PdfSearchDocumentProxy,
  query: string,
  options?: { signal?: AbortSignal },
): Promise<PdfSearchMatch[]> => {
  if (!query) {
    return []
  }

  const signal = options?.signal
  const pages = await getSearchPages(document)
  signal?.throwIfAborted()

  const matches: PdfSearchMatch[] = []
  let sliceStartedAt = performance.now()
  for (const page of pages) {
    for (const match of findPageMatches(page, query)) {
      matches.push(match)
    }
    if (performance.now() - sliceStartedAt > SEARCH_SLICE_BUDGET_MS) {
      await yieldToMain()
      signal?.throwIfAborted()
      sliceStartedAt = performance.now()
    }
  }
  return matches
}
